import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { bankIdLoginUrl } from "@/lib/bankid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Fingerprint, ShieldCheck } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [bankid, setBankid] = useState(null); // { enabled, only_for_users, provider_label }
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => {
    api.get("/auth/bankid/status", { skipActAs: true }).then((r) => setBankid(r.data)).catch(() => setBankid({ enabled: false }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const me = await login(email, password);
      navigate(me.role === "admin" ? "/admin" : "/", { replace: true });
    } catch (err) {
      setError(errorMessage(err, "Kunne ikke logge inn"));
    } finally {
      setBusy(false);
    }
  };

  const startBankId = () => {
    if (inviteToken) sessionStorage.setItem("rp_invite_token", inviteToken);
    window.location.assign(bankIdLoginUrl({ invite: inviteToken || undefined }));
  };

  const bankidEnabled = !!bankid?.enabled;
  const bankidOnly = bankidEnabled && !!bankid?.only_for_users;
  const passwordVisible = !bankidOnly || showPasswordForm;

  const passwordForm = (
    <form onSubmit={submit} className="space-y-5" noValidate data-testid="login-password-form">
      {bankidOnly ? <p className="text-xs text-muted-foreground" data-testid="login-admin-hint">Innlogging med e-post og passord er kun for administratorer.</p> : null}
      <div className="space-y-2">
        <Label htmlFor="email">E-postadresse</Label>
        <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" data-testid="login-email-input" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Passord</Label>
        <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11" data-testid="login-password-input" />
      </div>
      {error ? <p className="text-sm text-destructive" role="alert" data-testid="login-error">{error}</p> : null}
      <Button type="submit" className="w-full h-11" variant={bankidEnabled ? "outline" : "default"} disabled={busy} data-testid="login-submit-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Logg inn
      </Button>
    </form>
  );

  return (
    <AuthLayout
      title="Logg inn"
      subtitle={bankidEnabled ? "Velkommen tilbake. Logg inn trygt med BankID." : "Velkommen tilbake. Logg inn for å se regningene dine."}
      footer={<p>Har du ikke konto? <Link to={inviteToken ? `/opprett-konto?invite=${encodeURIComponent(inviteToken)}` : "/opprett-konto"} className="text-primary font-medium hover:underline" data-testid="go-to-register-link">Opprett konto</Link></p>}
    >
      <div className="space-y-5">
        {bankidEnabled ? (
          <div className="space-y-3" data-testid="login-bankid-section">
            <Button type="button" className="w-full h-12 text-base" onClick={startBankId} data-testid="login-bankid-button">
              <Fingerprint className="h-5 w-5 mr-2" /> Logg inn med BankID
            </Button>
            <p className="text-xs text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" /> Du identifiseres hos {bankid?.provider_label || "BankID-leverandøren"}. Controlle ser aldri BankID-passordet ditt, og fødselsnummeret lagres ikke i klartekst.</p>
          </div>
        ) : null}

        {bankidEnabled && passwordVisible ? (
          <div className="relative py-1"><div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div><div className="relative flex justify-center text-xs"><span className="bg-card px-2 text-muted-foreground">eller</span></div></div>
        ) : null}

        {passwordVisible ? passwordForm : (
          <button type="button" className="w-full text-center text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors" onClick={() => setShowPasswordForm(true)} data-testid="login-show-admin-form">
            Administrator? Logg inn med e-post og passord
          </button>
        )}

        {bankid && !bankidEnabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="outline" className="w-full h-11" disabled data-testid="login-bankid-button">
                <Fingerprint className="h-4 w-4 mr-2" /> Logg inn med BankID (kommer)
              </Button>
            </TooltipTrigger>
            <TooltipContent>BankID-innlogging aktiveres når administrator har lagt inn avtalen med BankID-leverandøren.</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </AuthLayout>
  );
}
